import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, CircularProgress, Alert, Button, Stack, Textarea } from '@mui/joy';
import { useParams } from 'react-router-dom';
import { declineMeeting } from '../../apiconfig/api';

interface MeetingData {
  applicantMeeting: {
    id: string;
    confirmedAt: string | null;
    declinedAt: string | null;
    note: string;
    meeting: {
      id: string;
      scheduledAt: string;
      location: string;
      duration: number;
      employer: {
        id: string;
        companyName: string;
        user: {
          id: string;
          fullName: string;
          email: string;
        };
      };
    };
    applicant: {
      id: string;
      user: {
        id: string;
        fullName: string;
        email: string;
        phoneNo: string;
      };
    };
  };
}

const DeclineMeeting: React.FC = () => {
  const { applicant_meeting_id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meetingData, setMeetingData] = useState<MeetingData | null>(null);
  const [note, setNote] = useState('');
  const [initialNote, setInitialNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  useEffect(() => {
    const handleDeclineMeeting = async () => {
      if (!applicant_meeting_id) {
        setError("Missing meeting ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const result = await declineMeeting(applicant_meeting_id, {});
        setMeetingData(result.data);
        setSuccess(true);
        
        // Pre-populate note if it exists
        if (result.data?.applicantMeeting?.note) {
          setNote(result.data.applicantMeeting.note);
          setInitialNote(result.data.applicantMeeting.note);
          setNoteSaved(true); // Note is already saved if it exists
        } else {
          setNoteSaved(false);
        }
      } catch (err: any) {
        console.error("Error declining meeting:", err);
        setError(err.message || "Failed to decline meeting. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    handleDeclineMeeting();
  }, [applicant_meeting_id]);

  // Reset noteSaved when note changes
  useEffect(() => {
    if (note.trim() !== initialNote) {
      setNoteSaved(false);
    }
  }, [note, initialNote]);

  const handleSaveNote = async () => {
    if (!applicant_meeting_id || !note.trim()) return;

    try {
      setSavingNote(true);
      setError(null);
      
      const result = await declineMeeting(applicant_meeting_id, { note: note.trim() });
      setMeetingData(result.data);
      setSuccess(true);
      setNoteSaved(true);
      setInitialNote(note.trim()); // Update initial note to current note
    } catch (err: any) {
      console.error("Error saving note:", err);
      setError(err.message || "Failed to save note. Please try again.");
    } finally {
      setSavingNote(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatTimeRange = (dateString: string, durationMs: number) => {
    const startDate = new Date(dateString);
    const endDate = new Date(startDate.getTime() + durationMs);
    
    const formatTime = (date: Date) => {
      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    const formatDate = (date: Date) => {
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);
    const date = formatDate(startDate);

    return `${date} between ${startTime} and ${endTime}`;
  };

  const formatDuration = (minutes: number) => {
    if (minutes === 60) return '1 hour';
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hours`;
    return `${hours} hours ${remainingMinutes} minutes`;
  };

  const textareaStyles = {
    backgroundColor: '#ECEFF3',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.125rem',
    color: '#1A1B1D',
    '--Textarea-focusedHighlight': 'transparent !important',
    '&::placeholder': {
      color: '#7B7F85 !important',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: '#ECEFF3',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: '#ECEFF3',
    },
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f4f4f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Container maxWidth="sm">
          <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: '16px', 
            p: 4, 
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <CircularProgress size="lg" sx={{ mb: 2 }} />
            <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              Declining your meeting...
            </Typography>
            <Typography level="body-md" sx={{ color: '#7B7F85', mt: 1 }}>
              Please wait while we process your decline.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#f4f4f4',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      <Container maxWidth="md">
        <Box sx={{ 
          bgcolor: 'white', 
          borderRadius: '16px', 
          p: 4, 
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          {error ? (
            <>
              <Alert color="danger" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
                Meeting Decline Failed
              </Typography>
              <Typography level="body-md" sx={{ color: '#7B7F85', mb: 3 }}>
                We encountered an issue while declining your meeting. Please try again or contact support if the problem persists.
              </Typography>
            </>
          ) : success && meetingData ? (
            <>
              <Box sx={{ 
                width: '64px', 
                height: '64px', 
                borderRadius: '50%', 
                bgcolor: '#EF4444', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}>
                <Typography sx={{ color: 'white', fontSize: '2rem' }}>✕</Typography>
              </Box>
              
              <Typography level="h3" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
                Meeting Declined
              </Typography>
              
              <Typography level="h4" sx={{ fontWeight: 500, color: '#7B7F85', mb: 3 }}>
                Hi {meetingData.applicantMeeting.applicant.user.fullName}
              </Typography>

              <Box sx={{ 
                bgcolor: '#F8F9FA', 
                borderRadius: '12px', 
                p: 3, 
                mb: 3,
                textAlign: 'left'
              }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
                      Meeting Details
                    </Typography>
                    <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                      <strong>Company:</strong> {meetingData.applicantMeeting.meeting.employer.companyName}
                    </Typography>
                    <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                      <strong>Date & Time:</strong> {formatTimeRange(meetingData.applicantMeeting.meeting.scheduledAt, meetingData.applicantMeeting.meeting.duration)}
                    </Typography>
                    <Typography level="body-md" sx={{ color: '#7B7F85' }}>
                      <strong>Location:</strong> {meetingData.applicantMeeting.meeting.location}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Typography level="body-md" sx={{ color: '#7B7F85', mb: 3 }}>
                We'll notify {meetingData.applicantMeeting.meeting.employer.companyName} that you've declined the meeting.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
                  Add a Note for {meetingData.applicantMeeting.meeting.employer.companyName}
                </Typography>
                <Stack spacing={2}>
                  <Textarea
                    value={note}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
                    placeholder={`Add any additional notes or questions for ${meetingData.applicantMeeting.meeting.employer.companyName}...`}
                    minRows={4}
                    sx={textareaStyles}
                  />
                  <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                    <Button
                      variant="solid"
                      onClick={handleSaveNote}
                      disabled={!note.trim() || savingNote || note.trim() === initialNote}
                      sx={{
                        backgroundColor: '#2e2d7e',
                        '&:hover': { backgroundColor: '#1e1d6e' },
                        '&:disabled': { 
                          backgroundColor: '#9CA3AF',
                          color: '#6B7280'
                        }
                      }}
                    >
                      {savingNote ? 'Saving...' : noteSaved ? 'Saved' : 'Save Note'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </>
          ) : null}
        </Box>
      </Container>
    </Box>
  );
};

export default DeclineMeeting; 